import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';

import { db } from '../firebase';
import AdminSidebar from '../components/AdminSidebar';
import '../style/common.css';
import './po-detail.css';
import POByJC from './POByJC';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function POdetail() {
  const { id } = useParams();
  const [po, setPO] = useState(null);
  const [assignedToName, setAssignedToName] = useState('Not assigned');
  const [raBills, setRABills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [vendorMaterials, setVendorMaterials] = useState([]);

  const [editingBillId, setEditingBillId] = useState(null);
  const [editForm, setEditForm] = useState({
    bill_name: '',
    bill_number: '',
    bill_date: '',
    amount: ''
  });

  const formatINR = (value) => {
    return value?.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const fetchPOAndRABills = async () => {
    try {
      const poRef = doc(db, 'purchase_orders', id);
      const poSnap = await getDoc(poRef);
  
      if (poSnap.exists()) {
        const poData = { id: poSnap.id, ...poSnap.data() };
  
        // Get RA bills
        const billQuery = query(
          collection(db, 'ra_bills'),
          where('po_id', '==', poData.id)
        );
        const billSnap = await getDocs(billQuery);
        const billList = billSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
  
        // Calculate total of all RA bill amounts
        const totalRAAmount = billList.reduce((acc, bill) => acc + Number(bill.amount || 0), 0);
        const balanceAmount = Number(poData.po_total_amount || 0) - totalRAAmount;
  
        setPO({ ...poData, balance_amount: balanceAmount });
        setRABills(billList);
  
        // Assigned to user
        if (poData.assigned_to) {
          const userRef = doc(db, 'users', poData.assigned_to);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const user = userSnap.data();
            setAssignedToName(user.name || user.email || 'Unnamed');
          }
        }
  
        // Vendors
        const vendorSnap = await getDocs(collection(db, `purchase_orders/${id}/vendors`));
        const vendorList = vendorSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVendors(vendorList);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error fetching PO details');
    }
  };
  

  useEffect(() => {
    fetchPOAndRABills();
  }, [id]);

  const handleVendorSelect = async (vendorId) => {
    setSelectedVendorId(vendorId);
    try {
      const materialSnap = await getDocs(collection(db, `purchase_orders/${id}/vendors/${vendorId}/materials`));
      const materials = materialSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendorMaterials(materials);
    } catch (err) {
      toast.error('Error fetching vendor materials');
    }
  };

  const startEditingBill = (bill) => {
    setEditingBillId(bill.id);
    setEditForm({
      bill_name: bill.bill_name || '',
      bill_number: bill.bill_number || '',
      bill_date: bill.bill_date || '',
      amount: bill.amount || ''
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };



const saveBillChanges = async () => {
  try {
    const billRef = doc(db, 'ra_bills', editingBillId);
    await updateDoc(billRef, {
      bill_name: editForm.bill_name,
      bill_number: editForm.bill_number,
      bill_date: editForm.bill_date,
      amount: Number(editForm.amount), // ensure it's stored as number
    });

    toast.success('RA Bill updated successfully!');
    setEditingBillId(null);
    fetchPOAndRABills(); // Refresh RA bills list
  } catch (error) {
    console.error('Error updating RA Bill:', error);
    toast.error('Failed to update RA Bill');
  }
};


  const cancelEdit = () => {
    setEditingBillId(null);
    setEditForm({ bill_name: '', bill_number: '', bill_date: '', amount: '' });
  };

  if (!po) return <div>Loading...</div>;

  return (
    <div className="dashboard-wrapper">
      <AdminSidebar />
      <div className="dashboard-content">
        <h2>PO Detail</h2>

        <div className="po-detail-card">
          <p><strong>PO Number:</strong> {po.po_number}</p>
          <p><strong>JC Number:</strong> {po.jc_number}</p>
          <p><strong>Client:</strong> {po.client || 'N/A'}</p>
          <p><strong>Location:</strong> {po.location}</p>
          <p><strong>Base Amount:</strong> ₹{formatINR(po.po_base_amount) || '0.00'}</p>
          <p>
            <strong>Tax %:</strong>{' '}
            {po.tax_percentage > 0
              ? po.tax_percentage
              : po.gst > 0
              ? po.gst
              : po.tax_percent > 0
              ? po.tax_percent
              : 0}
            %
          </p>
          <p><strong>Total Amount:</strong> ₹{formatINR(po.po_total_amount) || '0.00'}</p>
          {po.balance_amount !== undefined && (
            <p><strong>Balance Amount:</strong> ₹{formatINR(po.balance_amount)}</p>
          )}
          <p><strong>Date:</strong> {po.po_date}</p>
          <p><strong>Deadline:</strong> {po.deadline || 'N/A'}</p>
          <p><strong>Assigned To:</strong> {assignedToName}</p>
          <p><strong>Status:</strong>{' '}
            <span style={{ color: po.completed ? 'green' : 'orange', fontWeight: '600' }}>
              {po.completed ? 'Completed' : 'Active'}
            </span>
          </p>
          <p><strong>Description:</strong> {po.description || 'No description provided.'}</p>
        </div>

        <div className="ra-bills-section" style={{ marginTop: '2rem' }}>
          <h3>RA Bills</h3>
          {raBills.length === 0 ? (
            <p>No RA Bills submitted yet.</p>
          ) : (
            <div className="po-list-2">
              {raBills.map(bill => (
                <div className="po-card" key={bill.id}>
                  {editingBillId === bill.id ? (
                    <>
                      <p>
                        <label>Bill Name: </label>
                        <input
                          type="text"
                          value={editForm.bill_name}
                          onChange={(e) => handleEditChange('bill_name', e.target.value)}
                        />
                      </p>
                      <p>
                        <label>Invoice Number: </label>
                        <input
                          type="text"
                          value={editForm.bill_number}
                          onChange={(e) => handleEditChange('bill_number', e.target.value)}
                        />
                      </p>
                      <p>
                        <label>Bill Date: </label>
                        <input
                          type="date"
                          value={editForm.bill_date}
                          onChange={(e) => handleEditChange('bill_date', e.target.value)}
                        />
                      </p>
                      <p>
                        <label>Amount (₹): </label>
                        <input
                          type="number"
                          value={editForm.amount}
                          onChange={(e) => handleEditChange('amount', e.target.value)}
                        />
                      </p>
                      <button onClick={saveBillChanges}>Save</button>
                      <button onClick={cancelEdit}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <p><strong>Bill Name:</strong> {bill.bill_name || 'Unnamed'}</p>
                      <p><strong>Invoice Number:</strong> {bill.bill_number || '-'}</p>
                      <p><strong>Bill Date:</strong> {bill.bill_date || '-'}</p>
                      <p><strong>Amount:</strong> ₹{formatINR(bill.amount)}</p>
                      <p><strong>Submitted At:</strong> {new Date(bill.submitted_at).toLocaleDateString()}</p>
                      <button onClick={() => startEditingBill(bill)}>Edit</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="vendor-section" style={{ marginTop: '2rem' }}>
          <h3>Vendor Details</h3>
          <POByJC jcNumber={po.jc_number} />
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default POdetail;
